// Shared financial calculation functions used by both quotes and invoices

export interface DeductionItem {
  description: string;
  amount: number;
}

export interface FinanceTotals {
  netTotal: number;
  optionalTotal: number;
  discountAmount: number;
  discountedTotal: number;
  deductionsTotal: number;
  totalAfterDeductions: number;
  vatRateValue: number;
  vatAmount: number;
  grossTotal: number;
  depositPercent: number;
  invoicedTotal: number;
  invoicedNet: number;
}

// Calculate line item total
export function calculateLineItemTotal(
  amount: number,
  factor: number,
  unitPrice: number,
  discount: number,
): number {
  return amount * factor * unitPrice * (1 - discount / 100);
}

// Calculate group total from an array of items, filtering out title rows.
// Works with legacy entity items, Zero entity items, and PDF items.
export function calculateGroupTotal(
  items: Array<
    | { total: number; isTitle?: boolean }
    | {
        amount: number;
        factor: number;
        unit_price: number;
        discount: number;
        is_title: boolean;
      }
    | {
        amount: number;
        factor: number;
        unitPrice: number;
        discount: number;
        isTitle: boolean;
      }
  >,
): number {
  return items.reduce((sum, item) => {
    if ("is_title" in item && item.is_title) return sum;
    if ("isTitle" in item && item.isTitle) return sum;
    if ("total" in item) return sum + item.total;
    if ("unitPrice" in item) {
      return (
        sum +
        calculateLineItemTotal(
          item.amount,
          item.factor,
          item.unitPrice,
          item.discount,
        )
      );
    }
    return (
      sum +
      calculateLineItemTotal(
        item.amount,
        item.factor,
        item.unit_price,
        item.discount,
      )
    );
  }, 0);
}

type GroupCalculationItem =
  | { total: number; isTitle?: boolean }
  | {
      amount: number;
      factor: number;
      unit_price: number;
      discount: number;
      is_title: boolean;
    }
  | {
      amount: number;
      factor: number;
      unitPrice: number;
      discount: number;
      isTitle: boolean;
    };

export function calculateGroupGrossTotal(
  items: GroupCalculationItem[],
): number {
  return items.reduce((sum, item) => {
    if ("is_title" in item && item.is_title) return sum;
    if ("isTitle" in item && item.isTitle) return sum;
    if ("unitPrice" in item) {
      return sum + item.amount * item.factor * item.unitPrice;
    }
    if ("unit_price" in item) {
      return sum + item.amount * item.factor * item.unit_price;
    }
    return sum + item.total;
  }, 0);
}

export function calculateGroupDiscountTotal(
  items: GroupCalculationItem[],
): number {
  return calculateGroupGrossTotal(items) - calculateGroupTotal(items);
}

// Calculate totals for groups with discount, deductions, and VAT
export function calculateFinanceTotals(
  groups: Array<{
    is_optional?: boolean;
    isOptional?: boolean;
    apply_project_discount?: boolean;
    applyProjectDiscount?: boolean;
    items: Array<
      | { total: number; isTitle?: boolean }
      | {
          amount: number;
          factor: number;
          unit_price: number;
          discount: number;
          is_title: boolean;
        }
      | {
          amount: number;
          factor: number;
          unitPrice: number;
          discount: number;
          isTitle: boolean;
        }
    >;
  }>,
  projectDiscountPercent: number,
  vatRate: "CH_8_1" | "NONE",
  deductionItems: DeductionItem[] = [],
  depositPercent: number = 100,
): FinanceTotals {
  let netTotal = 0;
  let optionalTotal = 0;
  let discountEligibleTotal = 0;

  groups.forEach((group) => {
    const groupTotal = calculateGroupTotal(group.items);

    if (group.isOptional ?? group.is_optional ?? false) {
      optionalTotal += groupTotal;
    } else {
      netTotal += groupTotal;
      if (group.applyProjectDiscount ?? group.apply_project_discount ?? true) {
        discountEligibleTotal += groupTotal;
      }
    }
  });

  const discountAmount = discountEligibleTotal * (projectDiscountPercent / 100);
  const discountedTotal = netTotal - discountAmount;

  const deductionsTotal = deductionItems.reduce(
    (sum, item) => sum + item.amount,
    0,
  );
  const totalAfterDeductions = discountedTotal - deductionsTotal;

  const vatRateValue = vatRate === "CH_8_1" ? 8.1 : 0;
  const vatAmount = totalAfterDeductions * (vatRateValue / 100);
  const grossTotal = totalAfterDeductions + vatAmount;

  const effectiveDepositPercent =
    depositPercent > 0 && depositPercent <= 100 ? depositPercent : 100;
  const invoicedTotal =
    effectiveDepositPercent < 100
      ? Math.round(grossTotal * (effectiveDepositPercent / 100) * 100) / 100
      : grossTotal;
  const invoicedNet =
    effectiveDepositPercent < 100
      ? Math.round(
          totalAfterDeductions * (effectiveDepositPercent / 100) * 100,
        ) / 100
      : totalAfterDeductions;

  return {
    netTotal,
    optionalTotal,
    discountAmount,
    discountedTotal,
    deductionsTotal,
    totalAfterDeductions,
    vatRateValue,
    vatAmount,
    grossTotal,
    depositPercent: effectiveDepositPercent,
    invoicedTotal,
    invoicedNet,
  };
}
